import { useState } from 'react';
import { HelpCircle, X, Lightbulb, Zap, ChevronRight } from 'lucide-react';

const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };
const MONO  = { fontFamily: "'IBM Plex Mono', monospace" };

// ── Help content per page ─────────────────────────────────────────────────────
const GUIDE = {
  overview: {
    title: 'Dashboard Overview',
    description: 'Your recruitment intelligence command centre. Everything happening across your pipeline and account in one view.',
    features: [
      { label: 'Pipeline stats', desc: 'Live count of candidates by stage — Saved, Interest Sent, Accepted, Hired.' },
      { label: 'Hiring funnel', desc: 'Bar chart showing how candidates move through your stages.' },
      { label: 'Monthly activity', desc: 'Timeline of when candidates were added to your pipeline.' },
      { label: 'Quick actions', desc: 'Shortcuts to Gallery, AI Matchmaker, Talent Briefs and Compare.' },
    ],
    tips: [
      'Set your Hiring Focus in Account → Hiring Profile to get personalised match indicators on every candidate card.',
      'The "Matches your focus" badge on gallery cards is driven by your preferred roles setting.',
      'Run the AI Matchmaker with a real job description to surface the most relevant profiles instantly.',
    ],
  },
  pipeline: {
    title: 'Candidate Pipeline',
    description: 'Your Kanban board for tracking every candidate from first save through to hire. Drag cards between stages or use the action buttons.',
    features: [
      { label: 'Interest Sent', desc: 'Candidate has been expressed interest via Flence. Awaiting their response.' },
      { label: 'Accepted (green)', desc: 'Candidate accepted your interest on Flence. Contact details now visible on their card.' },
      { label: 'Unavailable (red)', desc: 'Candidate has temporarily paused availability. You\'ll be notified when they return.' },
      { label: 'Express Interest', desc: 'Click the mail icon on any card to send an interest expression via Flence — no direct email is sent.' },
    ],
    tips: [
      'Drag cards between columns to advance stages — stage changes fire events to Flence automatically.',
      'Once a candidate accepts your interest, their email appears in green on the card.',
      'Add a note to any card using the sticky note icon — useful for interview impressions.',
    ],
  },
  gallery: {
    title: 'Talent Gallery',
    description: 'Browse and filter the full pool of verified fashion professionals sourced via Flence. Every profile is AI-scored.',
    features: [
      { label: 'Filters', desc: 'Filter by role, seniority, city, country, availability and work preference.' },
      { label: 'AI Talent Brief', desc: 'Type a role description and get AI-ranked matches with scores (Intelligence plan).' },
      { label: 'Compare mode', desc: 'Select 2+ candidates for a side-by-side Intelligence Brief comparison.' },
      { label: 'Save to pipeline', desc: 'Click the bookmark icon on any card or drag it to the pipeline drop zone.' },
    ],
    tips: [
      'Use the AI Talent Brief at the top of the gallery for semantic matching — much more powerful than keyword filters.',
      'The gold star ranking (0-10) is FashionSpyder\'s overall intelligence score for that profile.',
      '"NEW" badges flag profiles added in the last 7 days.',
    ],
  },
  matchmaker: {
    title: 'AI Matchmaker',
    description: 'Paste or type a full job description and let the AI rank the most relevant candidates from the gallery.',
    features: [
      { label: 'GPT JD parsing', desc: 'The AI extracts role, skills, seniority and cultural requirements from your text.' },
      { label: 'Weight sliders', desc: 'Adjust how much Skills, Style DNA, Authority and Trend Alignment each count.' },
      { label: 'Why this match', desc: 'Each result card explains exactly why the AI ranked that candidate highly.' },
      { label: 'Add to pipeline', desc: 'Add any matched candidate directly to your pipeline from the results view.' },
    ],
    tips: [
      'More detail in the JD = better results. Include brand aesthetic cues like "editorial luxury" or "streetwear-forward".',
      'Use Find Similar on a candidate profile to surface profiles with a matching aesthetic DNA.',
      'Results are re-ranked by GPT after initial scoring — the top 5 are particularly reliable.',
    ],
  },
  searches: {
    title: 'Talent Briefs',
    description: 'Save your active role searches. FashionSpyder notifies you when a new candidate matching your criteria appears in the gallery.',
    features: [
      { label: 'Saved searches', desc: 'Store role + location + availability combinations as named briefs.' },
      { label: 'Match count', desc: 'Live count of candidates currently matching each brief\'s criteria.' },
      { label: 'Browse matches', desc: '"Browse Matches" opens the gallery pre-filtered to that brief\'s criteria.' },
      { label: 'Notifications', desc: 'Toggle the bell to receive an in-app alert when a new matching profile arrives.' },
    ],
    tips: [
      'Create briefs for each open role — you\'ll be notified automatically when new matches arrive.',
      'Use specific city + role + seniority combinations for the most targeted notifications.',
    ],
  },
  outreach: {
    title: 'Outreach Log',
    description: 'A record of every interest expression you have sent to candidates via Flence. Track their response status here.',
    features: [
      { label: 'Interest Sent', desc: 'Your introduction has been shared with the candidate on their Flence panel. Awaiting response.' },
      { label: 'Accepted', desc: 'The candidate accepted your interest. Their email is now visible below their name.' },
      { label: 'Unavailable', desc: 'The candidate has temporarily paused. Flence will notify FashionSpyder when they return.' },
    ],
    tips: [
      'Send outreach from the Pipeline — click the mail icon on any candidate card.',
      'A strong introduction mentioning specific work of theirs significantly increases acceptance rates.',
      'Your company logo and LinkedIn (set in Account) are shown to candidates on Flence before they accept.',
    ],
  },
  compare: {
    title: 'Compare & Decide',
    description: 'Place up to 4 candidates side by side for a detailed intelligence brief comparison before making a hiring decision.',
    features: [
      { label: 'Score grid', desc: 'DNA bars for Skill, Cultural and Trend alignment compared across candidates.' },
      { label: 'Attribute table', desc: 'Experience, availability, work mode, languages and location compared row by row.' },
      { label: 'Add to pipeline', desc: 'Add individual candidates or all of them to your pipeline directly from this view.' },
    ],
    tips: [
      'Select candidates for comparison using the checkbox on gallery cards.',
      'Compare works best when candidates are in the same role family for a fair comparison.',
    ],
  },
};

// ── Component ─────────────────────────────────────────────────────────────────
const PageHelp = ({ pageKey, className = '' }) => {
  const [open, setOpen] = useState(false);
  const content = GUIDE[pageKey];
  if (!content) return null;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors ${className}`}
        title="How does this work?"
        data-testid={`help-btn-${pageKey}`}
      >
        <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-xs hidden sm:inline" style={MONO}>Guide</span>
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Slide-in panel */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-sm bg-white border-l border-border z-50 flex flex-col overflow-hidden"
        style={{
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: open ? '-4px 0 24px rgba(0,0,0,0.08)' : 'none',
        }}
        data-testid={`help-panel-${pageKey}`}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-border flex items-start justify-between flex-shrink-0">
          <div>
            <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.18em' }} className="text-muted-foreground uppercase mb-1">
              Platform Guide
            </p>
            <h2 className="text-lg font-semibold" style={SERIF}>{content.title}</h2>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-muted-foreground hover:text-foreground mt-0.5 flex-shrink-0"
            data-testid={`help-panel-close-${pageKey}`}
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Description */}
          <p className="text-sm text-muted-foreground leading-relaxed">{content.description}</p>

          {/* Features */}
          <div>
            <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.16em' }} className="text-muted-foreground uppercase mb-3 flex items-center gap-1.5">
              <Zap className="w-3 h-3" strokeWidth={1.5} /> What's here
            </p>
            <div className="space-y-3">
              {content.features.map((f, i) => (
                <div key={i} className="flex gap-3">
                  <ChevronRight className="w-3 h-3 text-muted-foreground/40 flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs font-semibold text-foreground">{f.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div>
            <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.16em' }} className="text-muted-foreground uppercase mb-3 flex items-center gap-1.5">
              <Lightbulb className="w-3 h-3" strokeWidth={1.5} /> Tips
            </p>
            <div className="space-y-2.5">
              {content.tips.map((tip, i) => (
                <div key={i} className="bg-secondary/40 border border-border/60 px-3 py-2.5">
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0">
          <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }} className="text-muted-foreground/40 uppercase text-center">
            FashionSpyder Intelligence Platform
          </p>
        </div>
      </div>
    </>
  );
};

export default PageHelp;
